import React, { useContext, useEffect, useState, useMemo } from "react";
import Modal from 'react-bootstrap/Modal';
import { getConfigValue } from "../../util/getConfigValue";
import { AppContext } from '../../util/AppContext';
import PropType from "prop-types";
import { getSpecialtyPharmacies } from "../../API/specialtyPharmacy";
import { getSanitizedHtml } from "../../util/getSanitizedHtml";
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTableColumnsSpec } from '../../util/getTableColumnsSpec.js';

SpecialtyPharmacyModal.propTypes = {
    show: PropType.bool,
    setShow: PropType.func,
    plan: PropType.shape({
        data: PropType.shape({
            name: PropType.string,
            dimPlanIdentity: PropType.number,
        })
    })
};


function SpecialtyPharmacyModal({ show, setShow, plan }) {
    const [appState] = useContext(AppContext);
    const [specialtyData, setSpecialtyData] = useState(null);
    const [tableData, setTableData] = useState({ nodes: [] });
    const [viewport, setViewport] = useState({ width: window.innerWidth });
    const { name, dimPlanIdentity } = plan.data;

    useEffect(() => {
        function handleResize() {
            setViewport({ width: window.innerWidth });
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const columnsSpec = useMemo(() => {
        if (viewport.width < 990) {
            return '1fr';
        }
        if (specialtyData?.headers) {
            return getTableColumnsSpec(specialtyData.headers);
        }
        return '1fr';
    }, [specialtyData, viewport.width]);

    const theme = useTheme({
        Table: `
            --data-table-library_grid-template-columns: ${columnsSpec};
            border-radius: 4px;
            overflow: hidden;
        `,
        HeaderRow: `
            background-color: var(--bs-dark);
            color: var(--bs-white);
            font-weight: bold;
        `,
        Row: `
            &:nth-of-type(odd) {
                background-color: var(--bs-gray-100);
            }
            &:hover {
                background-color: var(--bs-gray-100);
            }
            border-bottom: 1px solid var(--bs-border-color);
        `,
        BaseCell: `
            padding: 12px 8px;
            font-size: 14px;
            word-break: break-word;

            & > div {
                white-space: normal;
            }
        `
    });

    useEffect(() => {
        const productId = appState?.currentProduct?.clientProductId;
        const planIdentity = dimPlanIdentity;
        const prescriberId = appState?.prescriber?.displayPrescriberId;
        const messageCode = appState?.category?.messageCode;
        const level = appState?.prescriberType;
        const messageTypeId = appState?.category?.messageTypeId;
        if (show) {
            getSpecialtyPharmacies(productId, planIdentity, prescriberId, messageCode, level, messageTypeId)
                .then((results) => {
                    setSpecialtyData(results);
                    if (results?.rows) {
                        setTableData({ nodes: results.rows });
                    }
                })
                .catch((err) => {
                    console.error('Failed to load specialty pharmacies:', err);
                });
        }
    }, [show]);

    return (
        <Modal show={show} className="modal-lg" onHide={() => setShow(false)} scrollable={true}>
            <Modal.Header className="primary" closeButton>
                <Modal.Title className="modal-title fs-5">{getConfigValue(appState.config?.SpecialtyPharmacy?.Modal.Title, appState)?.Value}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-body">
                <h2 className="type-20 color-grey-d1 mt-2 mb-3 pb-2">{name}</h2>

                {specialtyData?.headers && (
                    <Table
                        data={tableData}
                        theme={theme}
                        layout={{ custom: true, isDiv: true }}
                        className='table pd-table mb-0'
                    >
                        {(tableList) => (
                            <>
                                <Header>
                                    <HeaderRow className='table-dark d-none d-lg-contents'>
                                        {specialtyData.headers.map((header) => (
                                            <HeaderCell key={header.key} scope='col' className='px-2 py-2'>
                                                {header.title}
                                            </HeaderCell>
                                        ))}
                                    </HeaderRow>
                                </Header>
                                <Body>
                                    {tableList.map((item) => (
                                        <Row key={item.id} item={item} className="row pd-plan-table-row align-items-center">
                                            {specialtyData.headers.map((header) => (
                                                <Cell key={header.key} className='px-2 py-2'>
                                                    <span className='d-block d-lg-none color-grey-d1'>{item.columns[header.key] ? header.title : ""}</span>
                                                    <span className="form-check-label" dangerouslySetInnerHTML={getSanitizedHtml(item.columns[header.key])}></span>
                                                </Cell>
                                            ))}
                                        </Row>
                                    ))}
                                </Body>
                            </>
                        )}
                    </Table>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default SpecialtyPharmacyModal;